'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import { Scale, Shield, FileSearch, BookOpen, FileEdit } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';


const FEATURES = [
  {
    id: 'feat-case-mgmt',
    icon: Scale,
    title: 'Full Case Management',
    desc: 'Track hearings, timelines, and negotiations across all your cases in one place.',
  },
  {
    id: 'feat-doc-analysis',
    icon: FileSearch,
    title: 'AI Document Analysis',
    desc: 'Instantly extract clauses, risk flags, obligations, and leverage from court bundles.',
  },
  {
    id: 'feat-research',
    icon: BookOpen,
    title: 'UK Case Law Research',
    desc: 'Search BAILII and ICLR precedents with AI-ranked relevance scoring.',
  },
  {
    id: 'feat-drafting',
    icon: FileEdit,
    title: 'Legal Document Drafting',
    desc: 'Generate N244 applications, position statements, and WP letters instantly.',
  },
];

export default function AuthPage() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-[480px] shrink-0 p-10 text-white"
        style={{ background: 'linear-gradient(160deg, #1e3a5f 0%, #0f2140 60%, #0a1628 100%)' }}
      >
        <div>
          <div className="flex items-center gap-3 mb-10">
            <AppLogo size={40} />
            <span className="text-xl font-bold tracking-tight">LAICSAI regulation</span>
          </div>
          <h1 className="text-3xl font-700 leading-tight mb-4">
            The AI-powered legal assistant for McKenzie Friends
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-10">
            Purpose-built for lay advisors supporting litigants in person through UK court proceedings. Manage the full case workflow with confidence.
          </p>
          <div className="space-y-5">
            {FEATURES?.map((f) => {
              const Icon = f?.icon;
              return (
                <div key={f?.id} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={18} className="text-white/90" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm font-600 text-white mb-0.5">{f?.title}</p>
                    <p className="text-xs text-white/60 leading-relaxed">{f?.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/40 text-xs">
          <Shield size={13} aria-hidden="true" />
          <span>GDPR compliant · UK-hosted · End-to-end encrypted</span>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-primary">LAICSAI regulation</span>
          </div>

          {/* Tabs */}
          <div className="flex border border-border rounded-xl p-1 mb-8 bg-muted">
            <button
              onClick={() => setTab('login')}
              aria-pressed={tab === 'login'}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                tab === 'login' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setTab('signup')}
              aria-pressed={tab === 'signup'}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                tab === 'signup' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {tab === 'login' ? <LoginForm /> : <SignUpForm onSuccess={() => setTab('login')} />}
        </div>
      </div>
    </div>
  );
}